import type { AppState, View } from "./state";
import type { ReadingStatus } from "../models/types";
import { isTestMode } from "../services/storage";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HistoryState {
  view: View;
  bookId?: string;
}

interface RouterDeps {
  state: AppState;
  render: () => void;
  isDirty: () => boolean;
  showDiscardModal: () => Promise<boolean>;
}

interface ParsedRoute {
  view: View;
  bookId?: string;
  search?: string;
  filter?: string;
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let _deps: RouterDeps | null = null;
let _testMode = false;
let _currentPath = "/";
let _popstateInProgress = false;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize the router. Call once after library data is loaded.
 * Parses the current URL and sets state accordingly.
 * Registers the popstate listener.
 */
export function init(
  deps: RouterDeps,
  initialPath?: string,
): void {
  _deps = deps;
  _testMode = isTestMode();

  const path = initialPath ?? window.location.pathname;
  const search = window.location.search;

  const route = parseUrl(path, search);
  applyRoute(route);

  // Set initial history state on the current entry
  const historyState: HistoryState = {
    view: deps.state.view,
    bookId: deps.state.selectedBookId ?? undefined,
  };
  history.replaceState(historyState, "", buildUrl(_currentPath, currentQueryParams()));

  window.addEventListener("popstate", handlePopstate);
}

/**
 * Push a new URL onto the history stack.
 * Call this when the user navigates to a new view (click book, add, cancel, etc.)
 */
export function navigateTo(path: string): void {
  _currentPath = path;
  const url = buildUrl(path);
  const historyState: HistoryState = historyStateFromPath(path);
  history.pushState(historyState, "", url);
}

/**
 * Update the current URL without creating a new history entry.
 * Used for search/filter changes on the list view.
 */
export function replaceUrl(params: {
  search?: string;
  filter?: string | null;
}): void {
  const url = buildUrl("/", {
    search: params.search || undefined,
    filter: params.filter ?? undefined,
  });
  const historyState: HistoryState = { view: "list" };
  history.replaceState(historyState, "", url);
  _currentPath = "/";
}

/**
 * Build a URL string with optional query params and testMode preservation.
 */
export function buildUrl(
  path: string,
  params?: Record<string, string | undefined>,
): string {
  const url = new URL(path, window.location.origin);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value) {
        url.searchParams.set(key, value);
      }
    }
  }

  if (_testMode) {
    url.searchParams.set("testMode", "");
  }

  // Clean up the testMode param to be a bare key (no =)
  let result = url.pathname + url.search;
  result = result.replace("testMode=", "testMode");
  return result;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function parseUrl(pathname: string, search: string): ParsedRoute {
  const params = new URLSearchParams(search);

  // /book/new — must check before /book/:id
  if (pathname === "/book/new") {
    return { view: "add" };
  }

  // /book/:id
  const bookMatch = pathname.match(/^\/book\/(.+)$/);
  if (bookMatch) {
    return { view: "detail", bookId: bookMatch[1] };
  }

  // / — list view
  if (pathname === "/" || pathname === "") {
    return {
      view: "list",
      search: params.get("search") ?? undefined,
      filter: params.get("filter") ?? undefined,
    };
  }

  // Unknown path → treat as list
  return { view: "list" };
}

function applyRoute(route: ParsedRoute): void {
  if (!_deps) return;
  const { state } = _deps;

  state.formSnapshot = null;

  switch (route.view) {
    case "list":
      state.view = "list";
      state.selectedBookId = null;
      state.searchQuery = route.search ?? "";
      state.filterStatus = (route.filter as ReadingStatus) ?? null;
      _currentPath = "/";
      break;

    case "detail":
      state.view = "detail";
      state.selectedBookId = route.bookId ?? null;
      _currentPath = `/book/${route.bookId}`;
      break;

    case "add":
      state.view = "add";
      state.selectedBookId = null;
      _currentPath = "/book/new";
      break;

    case "notFound":
      state.view = "notFound";
      state.selectedBookId = null;
      _currentPath = "/";
      break;
  }
}

function currentQueryParams(): Record<string, string | undefined> | undefined {
  if (!_deps) return undefined;
  const { state } = _deps;
  if (state.view !== "list") return undefined;
  const params: Record<string, string | undefined> = {};
  if (state.searchQuery) params.search = state.searchQuery;
  if (state.filterStatus) params.filter = state.filterStatus;
  if (Object.keys(params).length === 0) return undefined;
  return params;
}

function historyStateFromPath(path: string): HistoryState {
  if (path === "/book/new") return { view: "add" };
  const bookMatch = path.match(/^\/book\/(.+)$/);
  if (bookMatch) return { view: "detail", bookId: bookMatch[1] };
  return { view: "list" };
}

async function handlePopstate(): Promise<void> {
  if (!_deps || _popstateInProgress) return;

  const { state, render, isDirty, showDiscardModal } = _deps;
  const previousPath = _currentPath;

  if (isDirty()) {
    _popstateInProgress = true;
    try {
      const discard = await showDiscardModal();
      if (discard) {
        // User chose to discard — navigate to the new URL
        const route = parseUrl(
          window.location.pathname,
          window.location.search,
        );
        applyRoute(route);
        render();
      } else {
        // User cancelled — re-push the previous route to restore the history entry
        const url = buildUrl(previousPath);
        const historyState = historyStateFromPath(previousPath);
        history.pushState(historyState, "", url);
        state.modal = null;
      }
    } finally {
      _popstateInProgress = false;
    }
  } else {
    // No dirty form — navigate immediately
    const route = parseUrl(window.location.pathname, window.location.search);
    applyRoute(route);
    render();
  }
}
